import { useState, useEffect, useMemo } from 'react';
import { ProductCarousel, ProductTileWithSwatches, Card, LinkProvider } from '@lululemon/b2b-ecom-pattern-library';
import categoryQueryParams from 'helpers/categoryQueryParam';
import { ConditionalLink } from 'helpers/cocoLink';
import { useProduct } from 'helpers/hooks/useProduct';
import { toColorSwatchesAndImages } from 'helpers/mappers/toColorSwatchesAndImages';
import { toProductCollectionList } from 'helpers/mappers/toProductCollectionList';
import { getPrice, getLocaleByCurrencyCode, renderChfPrice } from 'helpers/priceHelper';
import { findCategoryBySlug } from 'helpers/utils/categorySlugUtils';
import { isSwedishUser, isSwissUser } from 'helpers/utils/countryHelper';
import { HOME_PAGE } from 'helpers/utils/googleAnalyticsEvents';
import { FEATURE_FLAG_LIST, useFeatureFlags, useValidLocale } from 'hooks';
import { useAnalytics } from 'hooks/useAnalytics';
import { createPortal } from 'react-dom';
import Skeleton from 'react-loading-skeleton';
import { Product } from 'shared/types/product/Product';
import { useProductList } from 'ui/cdp/context';
import { useAccount, useBusinessUnit } from 'frontastic/index';
import styles from './category-carousel.module.scss';

export type CategoryData = {
  data: CategoryProductCarousel;
};

export type CategoryProductCarousel = {
  mobile: boolean;
  tablet: boolean;
  desktop: boolean;
  title: string;
  callToAction: string;
  webRoute: string;
  tilesPerPage: number;
  categorySlug: string;
  totalProducts: number;
  promotionTitle: string;
  promotionDescription: string;
  promotionImage: string;
  promotionTargetText: string;
  promotionTarget: string;
  showPromotionCard: boolean;
};

type CardProducts = Product & {
  retailPriceRange: string[];
  wholesalePriceRange: string[];
};

type TilesPerPage = 1 | 2 | 3 | 4;

type CategoryPromotionProductTileProps = {
  product: CardProducts;
  testId: string;
  shouldUseChfFormat: boolean;
  priceProps: ReturnType<typeof getPrice>;
  href: string;
  name: string;
  colors: ReturnType<typeof toColorSwatchesAndImages>;
  showColorsOnHover?: boolean;
  onClick?: (...args: unknown[]) => void;
  className?: string;
  imageProps?: Record<string, unknown>;
  imageRef?: (node: HTMLImageElement | null) => void;
  onSwatchClick?: (...args: unknown[]) => void;
  onProductClick?: (...args: unknown[]) => void;
};

// Swiss users need CHF prices rendered without a space between the currency code and amount
// (e.g. "CHF19.35"). The pattern library's standard Price component cannot produce that format,
// so custom-formatted markup is portalled into the tile's price area, mirroring the CDP tile.
const CategoryPromotionProductTile = ({
  product,
  testId,
  shouldUseChfFormat,
  priceProps,
  href,
  name,
  colors,
  showColorsOnHover,
  onClick,
  className,
  imageProps,
  imageRef,
  onSwatchClick,
  onProductClick,
}: CategoryPromotionProductTileProps) => {
  const [priceArea, setPriceArea] = useState<Element | null>(null);

  const customPriceComponent = useMemo(() => {
    if (!shouldUseChfFormat) return null;
    return renderChfPrice({
      prices: product.retailPriceRange,
      salePrices: product.wholesalePriceRange,
      classes: {
        prices: styles.retailPrice,
        salePrices: styles.wholesalePrice,
      },
    });
  }, [shouldUseChfFormat, product.retailPriceRange, product.wholesalePriceRange]);

  useEffect(() => {
    if (!shouldUseChfFormat) {
      setPriceArea(null);
      return;
    }
    const container = document.querySelector(`[data-testid="${testId}"]`);
    setPriceArea(container?.querySelector('[class*="productPrice"]') ?? null);
  }, [shouldUseChfFormat, testId]);

  return (
    <>
      <ProductTileWithSwatches
        data-testid={testId}
        showColorsOnHover={showColorsOnHover}
        className={className}
        imageProps={imageProps}
        imageRef={imageRef}
        colors={colors}
        href={href}
        name={name}
        onSwatchClick={onSwatchClick}
        onProductClick={onProductClick}
        onClick={onClick}
        priceProps={
          shouldUseChfFormat
            ? {
                classes: {
                  prices: styles.priceContainer,
                  salePrices: styles.priceContainer,
                },
                prices: [],
                salePrices: [],
              }
            : priceProps
        }
      />
      {shouldUseChfFormat && customPriceComponent && priceArea ? createPortal(customPriceComponent, priceArea) : null}
    </>
  );
};

const CategoryPromotionCarousel = ({ data }: CategoryData) => {
  const { isFeatureEnabled } = useFeatureFlags();
  const isColorSwatchEnabled = isFeatureEnabled(FEATURE_FLAG_LIST.COLOR_SWATCH);
  const isInternationalTaxDisplayEnabled = isFeatureEnabled(FEATURE_FLAG_LIST.INTERNATIONAL_TAX_DISPLAY);
  const DEFAULT_TILES_PER_PAGE = 4;
  const {
    title,
    tilesPerPage,
    categorySlug,
    totalProducts,
    promotionTitle,
    promotionDescription,
    promotionImage,
    promotionTargetText,
    promotionTarget,
    callToAction,
    webRoute,
    showPromotionCard,
  } = data;
  const tilePerPage: TilesPerPage =
    tilesPerPage > DEFAULT_TILES_PER_PAGE ? DEFAULT_TILES_PER_PAGE : (tilesPerPage as TilesPerPage);
  const { categories, getCategoryPromotionCarouselProducts } = useProduct();
  const [showCTA, setShowCTA] = useState(false);
  const [items, setItems] = useState<CardProducts[]>([]);
  const [isLoading, setLoading] = useState(false);
  const { account } = useAccount();
  const { businessUnit } = useBusinessUnit();
  const { limitStep } = useProductList();
  const locale = useValidLocale();
  const isSwedish = isSwedishUser(account, businessUnit);
  const isSwiss = isSwissUser(account, businessUnit);

  //category link with all query params
  const categoryParams = categoryQueryParams(account, limitStep, null);
  const CategoryTarget = webRoute ? `${webRoute}?${categoryParams}` : webRoute;
  //Google Analytics
  const { trackEvent, EVENT_CATEGORY } = useAnalytics();

  useEffect(() => {
    if (categories.length && webRoute) {
      const lastSlug = webRoute.split('/').filter(Boolean).pop();
      if (lastSlug) {
        const matchedCategory = findCategoryBySlug(lastSlug, categories);
        setShowCTA(!!matchedCategory);
      } else {
        setShowCTA(false);
      }
    }
  }, [categories, webRoute]);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      if (categorySlug && categories.length) {
        try {
          const categoryResult = categories.filter((item) => {
            return item.slug === categorySlug;
          });
          if (categoryResult?.length) {
            const categoryId: string = categoryResult[0]?.categoryId ?? '';
            const response: any = await getCategoryPromotionCarouselProducts(categoryId, totalProducts);
            setItems(response?.items || []);
            setLoading(false);
          }
        } catch (error) {
          setLoading(false);
        }
      }
    };
    fetchProducts();
  }, [categories.length]);

  if (!categorySlug || !items?.length) {
    return null;
  }

  const getPricePropsForProduct = (product: CardProducts) => {
    const currencyCode = product.variants?.find((v) => v.currencyCode)?.currencyCode;
    const shouldUseSwedishIntlLocale = Boolean(isInternationalTaxDisplayEnabled && isSwedish && currencyCode === 'SEK');
    const resolvedLocale = shouldUseSwedishIntlLocale ? getLocaleByCurrencyCode(currencyCode) : locale;
    return getPrice(product, resolvedLocale);
  };

  const carouselItems = items.map((product, index) => {
    const currencyCode = product.variants?.find((v) => v.currencyCode)?.currencyCode;
    const shouldUseChfFormat = Boolean(isInternationalTaxDisplayEnabled && isSwiss && currencyCode === 'CHF');

    return (
      <CategoryPromotionProductTile
        key={`category-promotion-${product?.name}-${index}`}
        product={product}
        testId={`category-promotion-carousel_product-${index}_test-id`}
        shouldUseChfFormat={shouldUseChfFormat}
        showColorsOnHover={false}
        colors={toColorSwatchesAndImages(product, isColorSwatchEnabled)}
        href={product?._url as string}
        name={product?.name as string}
        priceProps={getPricePropsForProduct(product)}
        onClick={() => {
          trackEvent(
            EVENT_CATEGORY.PRODUCT_INTERACTION,
            HOME_PAGE.PROMOTION_CAROUSEL_TILE_CLICKED(
              toProductCollectionList([product], 'product-carousal', 'inventory_grid'),
              product?.name ?? '',
            ),
          );
        }}
      />
    );
  });

  if (showPromotionCard) {
    carouselItems.push(
      <Card
        classes={{ container: styles.carousel__card, imageContainer: styles.cardWrapper }}
        ctaText={promotionTargetText}
        ctaURL={promotionTarget}
        heading={promotionTitle}
        data-testid={`category-promotion-carousel_card_test-id`}
        key={'promotion-card'}
        imageProps={
          promotionImage
            ? {
                src: promotionImage,
              }
            : undefined
        }
        subContent={promotionDescription}
        onClick={() => {
          trackEvent(
            EVENT_CATEGORY.COMPONENT_EVENT,
            HOME_PAGE.PROMOTION_CAROUSEL_CARD_CLICKED(promotionTargetText, promotionTarget),
          );
        }}
      />,
    );
  }

  return (
    <div className={styles.carousel}>
      {isLoading ? (
        <Skeleton count={1} className={styles.loading} />
      ) : (
        <LinkProvider component={ConditionalLink}>
          <ProductCarousel
            slidesPerPage={tilePerPage}
            title={title}
            onProductClick={() => {
              trackEvent(EVENT_CATEGORY.COMPONENT_EVENT, HOME_PAGE.SHOP_ALL_BUTTON(callToAction));
            }}
            callToAction={showCTA ? callToAction : undefined}
            webRoute={showCTA ? CategoryTarget : undefined}
          >
            {carouselItems}
          </ProductCarousel>
        </LinkProvider>
      )}
    </div>
  );
};

export default CategoryPromotionCarousel;
